import { useState } from 'react';
import { SEO } from '../components/SEO';
import { PageHero } from '../components/PageHero';
import { ScrollReveal, StaggerReveal, StaggerItem } from '../components/ScrollReveal';
import { DarkTexture } from '../components/DarkTexture';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { templates } from '../data/templates';
import { FileText, Download } from 'lucide-react';

const ZOHO_FORM_URL = 'https://forms.zohopublic.in/infocomp1/form/DPDPAblogContactForm/formperma/iYUj7Uc0iKPqCXfKDGNbC6srRQyAlxKC5QlSmbsnECQ';

const Templates = () => {
  const [active, setActive] = useState(null);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = active.file;
    link.download = '';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div>
      <SEO
        title="DPDPA Templates — Privacy Notice, Consent, DPIA, RoPA, Breach Response"
        description="Free DPDPA compliance templates: Section 5 privacy notices, consent checklists, DPIA workbooks, breach response playbooks, RoPA registers and rights-request responses."
        path="/templates"
      />
      <PageHero
        testId="templates-page-hero"
        kicker="Ready-to-use Documents"
        title="Templates"
        description="Notices, consent checklists, DPIAs, breach playbooks, RoPA registers and rights-request responses — download the starter set for your organisation."
      />
      <section className="relative bg-navy py-16 sm:py-24 px-6 sm:px-10 lg:px-16 overflow-hidden" data-testid="templates-grid-section">
        <DarkTexture orbs={false} />
        <StaggerReveal className="relative max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((t) => (
            <StaggerItem key={t.id}>
              <div data-testid={`template-card-${t.id}`} className="group relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-7 h-full flex flex-col overflow-hidden hover:border-cyan/30 hover:-translate-y-1.5 transition-all duration-300">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan via-cyan/40 to-transparent" />
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-5 group-hover:bg-cyan transition-colors duration-300">
                  <FileText className="text-cyan group-hover:text-navy transition-colors duration-300" size={22} />
                </div>
                <p className="font-mono text-[11px] uppercase tracking-widest text-white/30 mb-2">{t.audience}</p>
                <h3 className="font-serif text-xl text-white mb-3">{t.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed flex-1 mb-6">{t.description}</p>
                <button
                  onClick={() => setActive(t)}
                  data-testid={`template-request-${t.id}`}
                  className="inline-flex items-center gap-2 text-sm text-white font-semibold border-b border-transparent group-hover:border-cyan group-hover:text-cyan transition-colors w-fit"
                >
                  Download template <Download size={14} />
                </button>
              </div>
            </StaggerItem>
          ))}
        </StaggerReveal>
      </section>

      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent data-testid="template-download-dialog" className="bg-navy border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-white">{active?.title}</DialogTitle>
            <DialogDescription className="text-white/50">Share a few details below so our team can follow up with relevant DPDPA guidance, then download the template.</DialogDescription>
          </DialogHeader>
          <div className="bg-paper rounded-xl overflow-hidden" data-testid="template-download-zoho-form-wrapper">
            <iframe
              data-testid="template-download-zoho-form-iframe"
              title="DPDPA Blog Contact Form"
              src={ZOHO_FORM_URL}
              frameBorder="0"
              style={{ height: '620px', width: '100%', border: 'none', display: 'block' }}
            />
          </div>
          <button
            onClick={handleDownload}
            data-testid="template-download-submit-button"
            className="w-full bg-cyan text-navy py-3 rounded-lg font-semibold hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] transition-all inline-flex items-center justify-center gap-2"
          >
            Download Template <Download size={16} />
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Templates;
